import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { Capacitor } from '@capacitor/core';
import { getAndroidUpdateManifestUrl, isNewerAndroidBuild, resolveAndroidUpdateUrl } from '../../utils/androidUpdate';
import { toBase64FromArrayBuffer } from '../../utils/base64';

export async function installAndroidUpdateFlow({
  updatePayload,
  currentAndroidVersion,
  setAutoUpdateInProgress,
  setAutoUpdateStatus,
}) {
  const isNativeAndroid = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
  const manifestUrl = getAndroidUpdateManifestUrl();
  const absoluteApkUrl = resolveAndroidUpdateUrl(updatePayload?.apkUrl, manifestUrl);

  if (!isNativeAndroid || !updatePayload?.apkUrl) {
    window.open(absoluteApkUrl || updatePayload?.apkUrl, '_blank', 'noopener,noreferrer');
    return;
  }

  const updateId = String(updatePayload.versionCode || updatePayload.version || currentAndroidVersion || 'latest');
  setAutoUpdateInProgress(true);
  setAutoUpdateStatus('Update wordt gedownload...');

  try {
    const response = await fetch(absoluteApkUrl, { cache: 'no-store' });
    if (!response.ok) {
      throw new Error(`Download mislukt (${response.status})`);
    }

    const arrayBuffer = await response.arrayBuffer();
    const base64Data = toBase64FromArrayBuffer(arrayBuffer);
    const apkPath = `updates/robotbeheer-${updateId}.apk`;

    await Filesystem.writeFile({
      path: apkPath,
      data: base64Data,
      directory: Directory.Cache,
      recursive: true,
    });

    const fileUri = await Filesystem.getUri({ path: apkPath, directory: Directory.Cache });
    setAutoUpdateStatus('Download klaar. Installatiescherm wordt geopend...');

    await FileOpener.open({
      filePath: fileUri.uri,
      contentType: 'application/vnd.android.package-archive',
      openWithDefault: true,
    });

    setAutoUpdateStatus('Installatiescherm geopend. Bevestig de installatie.');
  } catch (error) {
    console.warn('Auto-update flow failed, fallback to browser download:', error);
    setAutoUpdateStatus('Automatisch installeren lukte niet. Download wordt geopend...');
    window.open(absoluteApkUrl || updatePayload.apkUrl, '_blank', 'noopener,noreferrer');
  } finally {
    setAutoUpdateInProgress(false);
  }
}

export async function downloadLatestAndroidApkFlow({ setAndroidUpdateInfo, setAutoUpdateStatus, onInstall, showToast }) {
  const manifestUrl = getAndroidUpdateManifestUrl();
  if (!manifestUrl) {
    showToast('❌ Geen update-manifest gevonden', 'error');
    return;
  }

  setAutoUpdateStatus('APK-download voorbereiden...');

  try {
    const requestUrl = `${manifestUrl}${manifestUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
    const response = await fetch(requestUrl, { cache: 'no-store' });
    if (!response.ok) throw new Error(`APK-download mislukt (${response.status})`);

    const payload = await response.json();
    if (!payload?.apkUrl) throw new Error('Manifest bevat geen APK URL');

    const dismissedKey = `androidUpdateDismissed:${payload.versionCode || payload.version || 'latest'}`;
    const updatePayload = { ...payload, dismissedKey };
    setAndroidUpdateInfo(updatePayload);
    await onInstall(updatePayload);
  } catch (error) {
    console.error('Directe APK-download mislukt:', error);
    setAutoUpdateStatus('APK-download mislukt. Probeer opnieuw.');
    showToast(`❌ APK downloaden mislukt: ${error?.message || String(error)}`, 'error');
  }
}

export async function fetchAndroidUpdateFlow({
  nativeAndroidVersion,
  nativeAndroidVersionCode,
  currentAndroidVersion,
  currentAndroidVersionCode,
  setAndroidUpdateInfo,
  setAutoUpdateStatus,
  onInstall,
  showToast,
}) {
  const manifestUrl = getAndroidUpdateManifestUrl();
  if (!manifestUrl) {
    showToast('❌ Geen update-manifest gevonden', 'error');
    return;
  }

  setAutoUpdateStatus('Controleren op update...');

  try {
    const requestUrl = `${manifestUrl}${manifestUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
    const response = await fetch(requestUrl, { cache: 'no-store' });
    if (!response.ok) throw new Error(`Update-check mislukt (${response.status})`);

    const payload = await response.json();
    if (!payload?.apkUrl) throw new Error('Manifest bevat geen APK URL');

    const hasUpdate = isNewerAndroidBuild({
      currentVersion: nativeAndroidVersion || currentAndroidVersion,
      currentVersionCode: nativeAndroidVersionCode || currentAndroidVersionCode,
      remoteVersion: payload.version,
      remoteVersionCode: payload.versionCode,
    });

    if (!hasUpdate) {
      setAutoUpdateStatus('Je gebruikt al de nieuwste versie.');
      showToast('✅ Je gebruikt al de nieuwste app-versie.', 'success');
      return;
    }

    const dismissedKey = `androidUpdateDismissed:${payload.versionCode || payload.version || 'latest'}`;
    const updatePayload = { ...payload, dismissedKey };
    setAndroidUpdateInfo(updatePayload);
    await onInstall(updatePayload);
  } catch (error) {
    console.error('Handmatige update-check mislukt:', error);
    setAutoUpdateStatus('Update-check mislukt. Probeer opnieuw.');
    showToast(`❌ Update ophalen mislukt: ${error?.message || String(error)}`, 'error');
  }
}
